import Content, { ContentNS } from "content/content";
import SubmitButton from "features/shared/components/buttons/submit-button/SubmitButton";
import LocalLink from "features/shared/components/link/LocalLink";
import useNavigateBackInfo, {
  PageType,
} from "features/shared/hooks/navigation/useNavigateBackInfo";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import Body from "features/shared/styles/typography/Body.styled";
import { TertiaryHeader } from "features/shared/styles/typography/TertiaryHeader.styled";
import Logging from "logging";
import { FC, useEffect, useRef, useState } from "react";
import { useAppStore } from "store/store";
import { useShallow } from "zustand/shallow";
import StyledAddressForm from "./styles/StyledAddressForm.styled";
import AddressCard from "./styles/AddressCard.styled";
import FormButtons from "./styles/FormButtons.styled";
import DataProvider from "services/data-provider/data-provider";
import DataProviderGeneralException, {
  DataProviderExceptionType,
} from "services/data-provider/data-provider-errors";
import SessionIneligibilityReason, {
  SessionIneligibilityReasonFromApi,
} from "models/session-ineligibility-reason";
import { TextInput } from "features/shared/components/text-input/TextInput";
import FromListPicker from "features/shared/components/picker/FromListPicker";
import { usaStateCodes } from "assets/usa-states/usa-states";
import usePaxLevelAnalytics from "features/shared/hooks/analytics/usePaxLevelAnalytics";

type LocalErrors = { [key: string]: string | null };

const AddressPage: FC = () => {
  const sessionId = useAppStore((state) => state.sessionId);

  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });
  const textUsaStates = (s: string) =>
    Content.shared.t(s, { ns: ContentNS.usaStates });

  const direction = Content.shared.instance.dir();

  const paxSession = usePaxSession();
  if (!paxSession) {
    throw new Error("invalid paxSession");
  }

  const paxSessionId = paxSession.paxSessionId;

  const formRef = useRef<HTMLFormElement | null>(null);

  const navigate = useNavigateWithQuery();

  const analyticsSendPageView = usePaxLevelAnalytics();

  const [updatePaxSession] = useAppStore(
    useShallow((state) => [state.updatePaxSession])
  );

  const [isLoading, setIsLoading] = useState(false);

  const setSessionIsLocked = useAppStore((state) => state.setSessionIsLocked);

  const backLinkInfo = useNavigateBackInfo(PageType.Address);

  const [errors, setErrors] = useState<LocalErrors>({});

  const setIneligibilityReason = useAppStore(
    (state) => state.setIneligibilityReason
  );

  const usaStatesOptions = usaStateCodes
    .map((key) => {
      return {
        id: key,
        value: textUsaStates(key),
      };
    })
    .sort((a, b) => a.value.localeCompare(b.value));

  useEffect(() => {
    analyticsSendPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    addressLine1,
    addressState,
    addressCity,
    addressPostalCode,
    setAddressLine1,
    setAddressState,
    setAddressCity,
    setAddressPostalCode,
  ] = useAppStore(
    useShallow((state) => [
      state.addressLine1,
      state.addressState,
      state.addressCity,
      state.addressPostalCode,
      state.setAddressLine1,
      state.setAddressState,
      state.setAddressCity,
      state.setAddressPostalCode,
    ])
  );

  return (
    <StyledAddressForm>
      {backLinkInfo ? (
        <LocalLink to={backLinkInfo.to} state={backLinkInfo.state} backLink>
          {backLinkInfo.title}
        </LocalLink>
      ) : null}

      <TertiaryHeader>{textUI("address-form.title")}</TertiaryHeader>
      <Body>{textUI("address-form.subtitle")}</Body>

      <AddressCard>
        <form ref={formRef}>
          <TextInput
            id="address-form-line1"
            value={addressLine1}
            label={textUI("address-form.line1")}
            error={errors.line1}
            onChange={(value) => setAddressLine1(value)}
          />

          <FromListPicker
            id="address-form-state"
            label={textUI("address-form.state")}
            options={usaStatesOptions}
            value={addressState}
            error={errors.state}
            onChange={(value) => setAddressState(value)}
          />

          <TextInput
            id="address-form-city"
            value={addressCity}
            label={textUI("address-form.city")}
            error={errors.city}
            onChange={(value) => setAddressCity(value)}
          />

          <TextInput
            id="address-form-postal-code"
            value={addressPostalCode}
            label={textUI("address-form.postal-code")}
            error={errors.postalCode}
            onChange={(value) => setAddressPostalCode(value)}
            maxLength={10}
          />
        </form>
        <FormButtons direction={direction} displayInline={false}>
          <SubmitButton onClick={() => onFormSubmit()} isLoading={isLoading}>
            {textUI("button.submit")}
          </SubmitButton>
        </FormButtons>
      </AddressCard>
    </StyledAddressForm>
  );

  function validateForm(): boolean {
    let formValid = true;

    const newErrors: LocalErrors = {};

    if (addressLine1.trim() === "") {
      newErrors["line1"] = textUI("question.answer-required");
      formValid = false;
    }

    if (addressState.trim() === "") {
      newErrors["state"] = textUI("question.answer-required");
      formValid = false;
    }

    if (addressCity.trim() === "") {
      newErrors["city"] = textUI("question.answer-required");
      formValid = false;
    }

    if (addressPostalCode.trim() === "") {
      newErrors["postalCode"] = textUI("question.answer-required");
      formValid = false;
    } else if (addressPostalCode.trim().length > 10) {
      newErrors["postalCode"] = textUI("address-form.postal-code-length-issue");
      formValid = false;
    }

    setErrors(newErrors);

    return formValid;
  }

  async function onFormSubmit() {
    if (isLoading) {
      return;
    }

    if (!(paxSession?.checklist && sessionId)) {
      Logging.log("questions", paxSessionId, "Missing checklist or session");
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    sendAddress(
      addressLine1.trim(),
      addressCity.trim(),
      addressState.trim(),
      addressPostalCode.trim(),
      () => {
        // redirect to /checklist for main flow navigation
        navigate("../checklist");
      }
    );
  }

  async function sendAddress(
    lineOne: string,
    city: string,
    state: string,
    postalCode: string,
    callback: () => any
  ) {
    try {
      const provider = DataProvider.shared;
      const updatedPaxSession = await provider.submitAddress(
        sessionId,
        paxSessionId,
        lineOne,
        undefined, // lineTwo
        undefined, // lineThree
        city,
        state,
        postalCode,
        "USA"
      );

      updatePaxSession(updatedPaxSession);
      callback();
    } catch (err) {
      setIsLoading(false);

      if (
        err instanceof DataProviderGeneralException &&
        err.type !== DataProviderExceptionType.Unknown &&
        err.type !== DataProviderExceptionType.BadRequest &&
        err.type !== DataProviderExceptionType.NotFound
      ) {
        // api "general" error
        switch (err.type) {
          case DataProviderExceptionType.NotProcessable:
          case DataProviderExceptionType.NotEligible:
            // not processable or not eligible
            setIneligibilityReason(
              SessionIneligibilityReasonFromApi(err.message)
            );
            return;

          case DataProviderExceptionType.SessionIsLocked:
            setSessionIsLocked(true);
            return;

          case DataProviderExceptionType.SessionIsFinalised:
            setIneligibilityReason(
              SessionIneligibilityReason.SessionIsFinalised
            );
            return;
        }
      }

      Logging.log(
        "address",
        paxSessionId,
        `Failed to submit address: ${err instanceof Error ? err.message : err}`
      );
    }
  }
};

export default AddressPage;
